import React from "react";
import Link from "next/link";
import moment from "@/lib/localizedMoment";
import variables from "@/common/globalVariables";
import Bubble from "@/common/UI/Bubble";
import Icon from "@/common/UI/Icon";
import FavoriteButton from "../JobFavoriteButton/FavoriteButton";
import Card from "@/common/UI/Card";
import sanitize from "@/lib/html";
import PrompToRegister from "@/components/users/PrompToRegister";
import Translator from "@/components/hoc/Translator";
import { numberWithCommas } from "../JobCompensationBubbles";
import JobPerksBubbles from "../JobPerksBubbles";
import { currency } from "@/root/config";
import SendToFriendButton from "../sendJob/SendToFriendButton";
import { useRouter } from "next/router";
import ApplyToJobButton from "@/common/UI/ApplyToJobButton";

const styles = ` background-color: ${variables.lightColor};
                margin: 20px auto;
                transition: 100ms;
                animation-timing-function: ease-in;
                padding: 20px !important;
                border-radius: 15px;
                width: 100%;
                `;


const jobListItem = (props) => {
  const router = useRouter();
  const { q, location, category } = router.query;
  const shortLocation = props.location.name;
  // get current url location
  const currentUrl = router.asPath;
  const noDescription = router.pathname !== "/";
  const compact = !!props.compact;

  const jobUrl = formatJobUrl(props.title, props.location.name, props.id);

  const isMedical =
    (q && q.toLowerCase().includes("medical")) ||
    (category && category.toLowerCase().includes("medical"));

  const cardStyles = compact
    ? ` background-color: ${variables.lightColor};
                margin: 0 0 12px;
                transition: 160ms ease;
                padding: 16px 16px 14px !important;
                border-radius: 12px;
                width: 100%;
                `
    : styles;

  return (
    <Card styles={cardStyles}>
      <div className={`JobListItemHeader${compact ? " compact" : ""}`}>
        <div>
          <Link href={jobUrl} legacyBehavior>
            <a className="JobTitle">
              <Translator>{props.title}</Translator>
            </a>
          </Link>
          <p className="Location">
            <Icon icon="marker" size="sm" className="LocationIcon" />{" "}
            {shortLocation}
          </p>
        </div>

        <div className="JobListItemMainInfo">
          {props.showPayRate && (
            <Bubble color={isMedical ? "5" : "1"}>
              {props.compensation > 0
                ? currency +
                  numberWithCommas(parseFloat(props.compensation).toFixed(2))
                : "DOE"}
            </Bubble>
          )}
          {props.showJobType && (
            <Bubble color={isMedical ? "5" : "1"}>
              <Translator>{props.type}</Translator>
            </Bubble>
          )}
        </div>
      </div>
      {!noDescription && (
        <Link href={jobUrl} legacyBehavior>
          <a className="Content">
            <Translator>
              {sanitize(props.description, [])
                ["__html"].substring(0, 300)
                .replace("&amp;", "and")}
              ...
            </Translator>
          </a>
        </Link>
      )}

      <div className="JobListItemFooter">
        {!noDescription && (
          <div className="row wrap">
            <JobPerksBubbles perks={props.perks} />
          </div>
        )}
        {compact ? (
          <>
            <p className="PostDate">
              <Translator>{moment(props.date).fromNow()}</Translator>
            </p>
            <div className="RelatedJobActions">
              <PrompToRegister>
                <SendToFriendButton jobId={props.id} />
              </PrompToRegister>
              {noDescription && (
                <div className="RelatedJobApply">
                  <ApplyToJobButton
                    jobId={props.id}
                    location={props.location.name}
                    compact
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="JobCardFooter">
              <p className="PostDate">
                <Translator>{moment(props.date).fromNow()}</Translator>
              </p>
              <PrompToRegister>
                <div className="JobCardActions">
                  <SendToFriendButton jobId={props.id} />
                  {!noDescription && (
                    <FavoriteButton
                      jobId={props.id}
                      count={props.favorites}
                      showFavoritesCount={props.showFavoritesCount}
                      inFlow
                    />
                  )}
                </div>
              </PrompToRegister>
            </div>
            {noDescription && (
              <div
                style={{
                  margin: "1.5rem 0rem 0rem 0rem",
                }}
              >
                <ApplyToJobButton
                  jobId={props.id}
                  location={props.location.name}
                />
              </div>
            )}
          </>
        )}
      </div>

      <style jsx>{`
        a {
          color: ${variables.darkColor};
          font-size: 1em;
          text-decoration: none;
        }

        .JobListItemHeader {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .JobListItemHeader .JobTitle {
          font-weight: 650;
          text-decoration: none;
          color: ${variables.accentColor2};
          font-size: ${compact ? "1.02em" : "1.2em"};
          line-height: 1.3;
        }

        .JobListItemHeader .JobTitle:hover {
          color: ${variables.accentColor1};
          border-bottom: 1px solid ${variables.accentColor1};
        }

        .JobListItemHeader.compact {
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 6px;
        }

        .JobListItemHeader.compact .JobListItemMainInfo {
          position: static;
        }

        .Location {
          font-weight: bold;
          margin: 5px 0 0;
        }

        .Content {
          font-weight: normal;
          font-size: 1.1em;

          line-height: 1.4em;
          margin-bottom: 10px;
          display: block;
        }

        .LocationIcon {
          opacity: 0.4;
        }

        .LikeIcon {
          opacity: 0.4;
        }

        .JobListItemFooter {
          display: flex;
          flex-direction: column;
        }

        .JobListItemFooter .row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
          margin-top: 5px;
          width: 100%;
        }

        .JobListItemFooter .row.wrap {
          flex-wrap: wrap;
          justify-content: flex-start;
        }

        .JobListItemFooter a {
          font-weight: bold;
          text-decoration: none;
        }

        .PostDate {
          font-size: 0.9em;
          font-weight: bold;
          color: ${isMedical ? variables.accentColor5 : variables.accentColor1};
          margin: 0;
        }

        .JobCardFooter {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 8px;
          width: 100%;
        }

        .JobCardFooter :global(.PromptToRegister) {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }

        .JobCardActions {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
        }

        .JobCardActions :global(.FavoriteButtonWrapper),
        .JobCardActions :global(.AnimatedButton) {
          position: relative !important;
        }

        .RelatedJobActions {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 10px;
          width: 100%;
        }

        .RelatedJobActions > :global(*) {
          width: 100%;
          display: block;
        }

        .RelatedJobActions :global(.ui.button) {
          width: 100%;
          justify-content: center;
          margin: 0;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .RelatedJobApply :global(.Button) {
          width: 100%;
          height: 40px;
          font-size: 0.95em;
          border-radius: 10px;
        }

        @media (max-width: 720px) {
          .JobListItemHeader {
            margin-top: 10px;
          }

          .JobListItemMainInfo {
            position: absolute;
            right: 0;
            top: 0;
          }

          .JobListItemHeader.compact .JobListItemMainInfo {
            position: static;
          }

          .JobListItemMainInfo :global(> span:nth-child(2)) {
            margin-left: 0;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }

          .JobListItemMainInfo :global(> span:nth-child(1)) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-top-left-radius: 0;
          }
        }
      `}</style>
    </Card>
  );
};

export default jobListItem;

export function formatJobUrl(title, location, id) {
  return `/jobs/${title.replace(/[\W_]+/g, "-")}-${location.replace(
    /[\W_]+/g,
    "-"
  )}-${id}`;
}
